import { useState, useEffect, Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  FormHelperText,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Equipment } from "../../types/equipments";
import {
  AttachmentOrigin,
  JobTask,
  JobStatusTimeline,
  JobUsedPart,
  StatusTransitionRequiredAction,
  StatusTransitionRequiredField,
} from "../../types/jobs";
import axios from "../../api/axios";
import { useIsServiceRole, useTenantName } from "../../contexts/auth";
import { useAttachmentRemovals } from "../../hooks/useAttachmentRemovals";
import CustomAutocomplete, { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import TaskServicesField, {
  inProgressHours,
  TaskServiceOption,
  toServiceQuantities,
  toTaskServiceOptions,
} from "./taskServicesField.component";
import FileDropZoneField from "../ui/fileDropZoneField/fileDropZoneField.component";
import EquipmentCounterForm, {
  PreviousCountersTable,
  useEquipmentLastCounters,
} from "../ui/equipmentCounterForm/equipmentCounterForm.component";
import {
  ATTACHMENTS_ENDPOINT,
  ERROR_SNACKBAR,
  JOB_ISSUES_ENDPOINT,
  PARTS_ENDPOINT,
} from "../../consts";
import { formatAddress, formatCounterValue, formatDecimalForInput, toApiDecimal } from "../../utils";

export interface JobStatusTransitionSaveDataPayload {
  task_updates: Array<{
    id: string;
    services?: Array<string | number>;
    service_quantities?: Record<string, string>;
    issues?: Array<string | number>;
    description?: string;
    remarks?: string;
    attachment_ids?: number[];
  }>;
  counter_entries: Array<{ equipment_id: string | number; kind: string; value: string }>;
  used_part_entries?: Array<{
    job_task_id: string | number;
    part_id: string | number;
    quantity: string;
  }>;
}

type AttachmentChip = {
  id: number;
  label: string;
  url?: string;
  origin?: AttachmentOrigin;
  // True once the file is bound to the task server-side. Removing one of those
  // only takes effect when the form is saved - see useAttachmentRemovals.
  persisted?: boolean;
};

interface JobStatusTransitionActionsModalProps {
  jobId: string;
  tasks: JobTask[];
  usedParts?: JobUsedPart[];
  initialRejectionReason?: string | null;
  requiredActions: StatusTransitionRequiredAction[];
  onSave: (payload: JobStatusTransitionSaveDataPayload) => Promise<boolean>;
  saving: boolean;
  onConfirm: (actionsData: Record<string, string>) => void;
  onClose: () => void;
  loading: boolean;
  fieldErrors?: Record<string, string[] | string>;
  // Status the job is being moved to - used to only warn about tasks missing
  // equipment when heading into verification, where it's easy to forget.
  desiredStatus?: string | null;
  // The job's time per status - its time "In progress" is offered as the
  // quantity of services billed by the hour.
  statusTimeline?: JobStatusTimeline | null;
}

const getRequiredKindsForEquipment = (equipment: Equipment): Array<"BP" | "CP"> => {
  if (equipment.kind === "C") {
    return ["BP", "CP"];
  }

  return ["BP"];
};

const equipmentLabel = (equipment: Equipment | null, t: (key: string) => string) =>
  equipment?.full_name || equipment?.serial || t("No equipment");

const ObligatoryChip = ({
  isRequired,
  reason,
  t,
}: {
  isRequired: boolean;
  // Why an optional field is obligatory here - the task's services.
  reason?: string;
  t: (key: string) => string;
}) => (
  <>
    <Chip
      size="small"
      label={isRequired ? t("Obligatory") : t("Optional")}
      color={isRequired ? "warning" : "default"}
      variant="outlined"
      sx={{ ml: 1 }}
    />
    {reason && (
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        {reason}
      </Typography>
    )}
  </>
);

const parseCounterValue = (value: unknown): number | null => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }

  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const COUNTER_KIND_LABELS: Record<string, string> = {
  BP: "Black pages",
  CP: "Color pages",
  SP: "Scanned pages",
};

interface EquipmentCounterFieldsProps {
  equipment: Equipment;
  assignedCounters: JobTask["counters"];
  jobId: string;
  onChange: (kind: string, data: Record<string, unknown>) => void;
}

const EquipmentCounterFields = ({ equipment, assignedCounters, jobId, onChange }: EquipmentCounterFieldsProps) => {
  const { t } = useTranslation();
  // A task can have several counters assigned (one per kind). If it has any
  // at all, those are the source of truth for the kinds they cover and the
  // "previous counters" reference table is hidden.
  const hasAssignedCounter = assignedCounters.length > 0;
  const lastCounters = useEquipmentLastCounters(equipment.id);
  const requiredKinds = getRequiredKindsForEquipment(equipment);
  const [valuesByKind, setValuesByKind] = useState<Record<string, unknown>>({});
  const [showScannedPages, setShowScannedPages] = useState(false);

  const handleChange = (kind: string, data: Record<string, unknown>, includeInSum: boolean) => {
    if (includeInSum) {
      setValuesByKind((prev) => ({ ...prev, [kind]: data.value }));
    }
    onChange(kind, data);
  };

  const handleHideScannedPages = () => {
    setShowScannedPages(false);
    onChange("SP", { equipment_id: equipment.id, kind: "SP", value: "" });
  };

  const parsedValues = requiredKinds.map((kind) => parseCounterValue(valuesByKind[kind]));
  const sum = parsedValues.reduce<number | null>(
    (total, value) => (value === null ? total : (total ?? 0) + value),
    null
  );

  const renderKindRow = (kind: string, includeInSum: boolean, onRemove?: () => void) => {
    // If this task already has an assigned counter of this kind, that's the
    // value to show/edit. Otherwise, prefill from the equipment's last known
    // reading only when the task has no assigned counter at all yet - once one
    // exists, it's the source of truth and stale global hints are misleading.
    const assignedMatch = assignedCounters.find((counter) => counter.kind === kind);
    const last = assignedMatch
      ? { value: Number(assignedMatch.value) }
      : !hasAssignedCounter
      ? lastCounters.find((counter) => counter.kind === kind)
      : undefined;
    return (
      <Box key={kind} sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          {t(COUNTER_KIND_LABELS[kind] ?? kind)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <EquipmentCounterForm
              key={`${kind}-${last ? "loaded" : "pending"}`}
              equipmentId={equipment.id}
              jobId={jobId}
              hiddenFields={["job", "equipment_id", "kind", "read_at"]}
              hideSubmit
              hidePreviousCountersTable
              hideLastValueHint
              wideFields
              initialData={{ kind, value: last ? String(last.value) : "" }}
              onFormDataChange={(data) => handleChange(kind, data, includeInSum)}
            />
          </Box>
          {onRemove && (
            <IconButton size="small" onClick={onRemove}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {!hasAssignedCounter && <PreviousCountersTable equipmentId={equipment.id} />}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {requiredKinds.map((kind) => renderKindRow(kind, true))}
      </Box>
      {requiredKinds.length > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            bgcolor: "action.hover",
            alignSelf: "flex-start",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {t("Total pages")}: {sum === null ? "—" : formatCounterValue(sum)}
          </Typography>
        </Box>
      )}
      {showScannedPages ? (
        renderKindRow("SP", false, handleHideScannedPages)
      ) : (
        <Button
          size="small"
          variant="text"
          startIcon={<AddIcon />}
          onClick={() => setShowScannedPages(true)}
          sx={{ alignSelf: "flex-start" }}
        >
          {t("Add scanned pages")}
        </Button>
      )}
    </Box>
  );
};

type TaskSubFieldKey =
  | "services"
  | "issues"
  | "counters"
  | "description"
  | "remarks"
  | "usedParts"
  | "attachments";

type TaskSubField = { key: TaskSubFieldKey; field: StatusTransitionRequiredField };

const SUB_FIELD_KEY_BY_TYPE: Record<string, TaskSubFieldKey> = {
  S: "services",
  I: "issues",
  V: "counters",
  T: "description",
  N: "remarks",
  U: "usedParts",
  A: "attachments",
};

const JobStatusTransitionActionsModal = ({
  jobId,
  tasks,
  usedParts = [],
  initialRejectionReason,
  requiredActions,
  onSave,
  saving,
  onConfirm,
  onClose,
  loading,
  fieldErrors,
  desiredStatus,
  statusTimeline,
}: JobStatusTransitionActionsModalProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const attachmentRemovals = useAttachmentRemovals();
  // A technician may add their own files but not take away the ones the job
  // was reported with - those are somebody else's account of the fault.
  const isServiceRole = useIsServiceRole();
  // Equipment with no installation address is back with us, not at the
  // client's site.
  const tenantName = useTenantName();

  // Tasks without equipment are silently excluded from the backend's own
  // counter/equipment validation for this transition, so nothing stops a job
  // from reaching verification with an equipment-less task - surface it here
  // instead, since this is the last stop before that happens.
  const tasksMissingEquipment = tasks.filter((task) => !task.equipment);
  const showMissingEquipmentWarning =
    desiredStatus === "FV" && tasksMissingEquipment.length > 0;

  const requiredFields = requiredActions[0]?.required_fields ?? [];
  const servicesField = requiredFields.find((f) => f.field_type === "S");
  const issuesField = requiredFields.find((f) => f.field_type === "I");
  const countersField = requiredFields.find((f) => f.field_type === "V");
  const usedPartsField = requiredFields.find((f) => f.field_type === "U");
  const descriptionField = requiredFields.find((f) => f.field_type === "T");
  const rejectionReasonField = requiredFields.find((f) => f.field_type === "R");
  const attachmentsField = requiredFields.find((f) => f.field_type === "A");
  const remarksField = requiredFields.find((f) => f.field_type === "N");

  // What a task with one of a requirement's services must have before the job
  // can move on - asked only in the cards of those tasks.
  const serviceRequirements = requiredActions[0]?.service_requirements ?? [];

  // The transition's own fields, rendered in every task card. Used parts are
  // tied to a specific task (it may represent external work with no
  // equipment) rather than the job as a whole, so they render here too instead
  // of as one job-wide block.
  const transitionSubFields = [
    servicesField ? { key: "services" as TaskSubFieldKey, field: servicesField } : null,
    issuesField ? { key: "issues" as TaskSubFieldKey, field: issuesField } : null,
    countersField ? { key: "counters" as TaskSubFieldKey, field: countersField } : null,
    descriptionField ? { key: "description" as TaskSubFieldKey, field: descriptionField } : null,
    remarksField ? { key: "remarks" as TaskSubFieldKey, field: remarksField } : null,
    usedPartsField ? { key: "usedParts" as TaskSubFieldKey, field: usedPartsField } : null,
    // Files document the machine they were taken of, so they are collected
    // per task alongside its services/issues, not as one job-wide block.
    attachmentsField ? { key: "attachments" as TaskSubFieldKey, field: attachmentsField } : null,
  ].filter(Boolean) as TaskSubField[];
  const transitionFieldTypes = new Set<string>(transitionSubFields.map(({ field }) => field.field_type));

  const [taskServices, setTaskServices] = useState<Record<string, TaskServiceOption[]>>({});
  const [taskIssues, setTaskIssues] = useState<Record<string, OptionRecord[]>>({});
  const [taskDescriptions, setTaskDescriptions] = useState<Record<string, string>>({});
  const [taskRemarks, setTaskRemarks] = useState<Record<string, string>>({});
  // Whether each task's remarks field is unfolded. Folded behind "Add remarks"
  // unless the task already has some - most visits leave none.
  const [remarksOpen, setRemarksOpen] = useState<Record<string, boolean>>({});
  // The task whose remarks were just unfolded by hand, so its field takes focus.
  const [remarksFocusTaskId, setRemarksFocusTaskId] = useState<string | null>(null);
  const [counterFormData, setCounterFormData] = useState<Record<string, Record<string, unknown>>>({});
  const [usedPartRowsByTask, setUsedPartRowsByTask] = useState<
    Record<string, Array<{ part: OptionRecord | null; quantity: string }>>
  >({});
  const [rejectionReason, setRejectionReason] = useState<string>(initialRejectionReason ?? "");
  const [attachmentChipsByTask, setAttachmentChipsByTask] = useState<
    Record<string, AttachmentChip[]>
  >({});
  const [attachmentUploadingTaskId, setAttachmentUploadingTaskId] = useState<string | null>(null);

  useEffect(() => {
    setRejectionReason(initialRejectionReason ?? "");
  }, [initialRejectionReason]);

  // Files already attached to a task show up as chips too, so the tech sees
  // what is there before adding more (and can drop one that was a mistake).
  // Saving refetches the job and re-runs this, so anything the user has taken
  // off the list is filtered out - otherwise a chip they just removed would
  // reappear while the refreshed job is still in flight.
  const { removedIds } = attachmentRemovals;
  useEffect(() => {
    setAttachmentChipsByTask(
      Object.fromEntries(
        tasks.map((task) => [
          task.id,
          (task.attachments ?? [])
            .filter((attachment) => !removedIds.includes(attachment.id))
            .map((attachment) => {
              const url = attachment.attachment || "";
              return {
                id: attachment.id,
                label: url ? url.replace(/^.*\//, "") : t("Attachment"),
                url,
                origin: attachment.origin ?? null,
                persisted: true,
              };
            }),
        ])
      )
    );
  }, [tasks, t, removedIds]);

  useEffect(() => {
    setTaskServices(Object.fromEntries(tasks.map((task) => [task.id, toTaskServiceOptions(task.services)])));
    setTaskIssues(Object.fromEntries(tasks.map((task) => [task.id, task.issues ?? []])));
    setTaskDescriptions(Object.fromEntries(tasks.map((task) => [task.id, task.description ?? ""])));
    setTaskRemarks(Object.fromEntries(tasks.map((task) => [task.id, task.remarks ?? ""])));
    setRemarksOpen(Object.fromEntries(tasks.map((task) => [task.id, Boolean(task.remarks)])));
  }, [tasks]);

  // Pre-fill with the job's already-used parts, grouped by the task they
  // were used on, so reopening the form (e.g. after a "save progress" click)
  // shows what was actually saved instead of an empty list.
  useEffect(() => {
    const grouped: Record<string, Array<{ part: OptionRecord | null; quantity: string }>> = {};
    usedParts.forEach((usedPart) => {
      const taskId = String(usedPart.job_task_id);
      if (!grouped[taskId]) {
        grouped[taskId] = [];
      }
      grouped[taskId].push({
        part: usedPart.part as OptionRecord,
        quantity: formatDecimalForInput(usedPart.quantity),
      });
    });
    setUsedPartRowsByTask(grouped);
  }, [usedParts]);

  const errorText = (rawError: string[] | string | undefined) =>
    !rawError ? null : Array.isArray(rawError) ? rawError.join(" ") : rawError;

  // What a requirement by service reports for one task, keyed
  // "service:<field type>:<task id>".
  const requirementErrorFor = (fieldType: string, taskId: string) =>
    errorText(fieldErrors?.[`service:${fieldType}:${taskId}`]);

  // The field's own errors, then a requirement's for this task alone. A field
  // shown only for a requirement has no id of its own (see subFieldsFor).
  const getFieldErrorMessage = (field: StatusTransitionRequiredField, taskId?: string) => {
    const messages = [
      field.id > 0 ? errorText(fieldErrors?.[String(field.id)]) : null,
      taskId ? requirementErrorFor(field.field_type, taskId) : null,
    ].filter(Boolean);

    return messages.length > 0 ? messages.join(" ") : null;
  };

  // Titles of the task's services that a requirement on this field asks for.
  // Read from the services picked here, so it follows them as they change.
  const servicesRequiring = (fieldType: string, task: JobTask): string[] => {
    const taskServiceIds = new Set(
      (taskServices[task.id] ?? task.services ?? []).map((service) => String(service.id))
    );
    return serviceRequirements
      .filter((requirement) => requirement.field_type === fieldType)
      .flatMap((requirement) => requirement.services)
      .filter((service) => taskServiceIds.has(String(service.id)))
      .map((service) => service.title);
  };

  const requiredForServicesText = (serviceTitles: string[]) =>
    t(
      serviceTitles.length === 1
        ? "Required for the service: {{services}}."
        : "Required for the services: {{services}}.",
      { services: serviceTitles.join(", ") }
    );

  const isRequiredFor = (field: StatusTransitionRequiredField, task: JobTask) =>
    field.is_required || servicesRequiring(field.field_type, task).length > 0;

  // The fields one task's card shows: the transition's own, plus any a
  // requirement asks of this task alone. Obligatory ones first; remarks right
  // under the description whether or not either is obligatory.
  const subFieldsFor = (task: JobTask): TaskSubField[] => {
    const requirementOnly = serviceRequirements
      .filter(
        (requirement) =>
          requirement.field_type !== "E" &&
          !transitionFieldTypes.has(requirement.field_type) &&
          servicesRequiring(requirement.field_type, task).length > 0
      )
      .map((requirement) => ({
        key: SUB_FIELD_KEY_BY_TYPE[requirement.field_type],
        field: {
          // Not a field of the transition, so no id of its own - its errors
          // come keyed by field type instead.
          id: -requirement.id,
          field_type: requirement.field_type as StatusTransitionRequiredField["field_type"],
          is_required: false,
          checkbox_label: null,
        },
      }));
    const sorted = [...transitionSubFields, ...requirementOnly].sort(
      (a, b) => Number(isRequiredFor(b.field, task)) - Number(isRequiredFor(a.field, task))
    );

    const remarks = sorted.find(({ key }) => key === "remarks");
    if (!remarks || !sorted.some(({ key }) => key === "description")) {
      return sorted;
    }
    return sorted
      .filter(({ key }) => key !== "remarks")
      .flatMap((subField) => (subField.key === "description" ? [subField, remarks] : [subField]));
  };

  // Equipment is set in the job, not here, so a requirement for it is a
  // notice in the task's card rather than a field.
  const equipmentRequiredBy = (task: JobTask) => (task.equipment ? [] : servicesRequiring("E", task));

  const handleAttachmentFile = async (taskId: string, file: File) => {
    if (!file) {
      return;
    }

    setAttachmentUploadingTaskId(taskId);
    try {
      const uploadData = new FormData();
      uploadData.append("attachment", file);
      const { data } = await axios.post(ATTACHMENTS_ENDPOINT, uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.id != null) {
        const url = typeof data.attachment === "string" ? data.attachment : "";
        const label = url ? url.replace(/^.*\//, "") : file.name;
        setAttachmentChipsByTask((prev) => ({
          ...prev,
          [taskId]: [
            ...(prev[taskId] ?? []),
            // Not bound to the task until this form is saved.
            { id: data.id, label, url, origin: data.origin ?? null, persisted: false },
          ],
        }));
      }
    } catch {
      enqueueSnackbar(t("Failed to upload file."), ERROR_SNACKBAR);
    } finally {
      setAttachmentUploadingTaskId(null);
    }
  };

  // A file that is already on the task is only taken off the list here; it is
  // deleted once the form is saved, so closing it instead puts the file back.
  const handleRemoveAttachment = async (taskId: string, chip: AttachmentChip) => {
    const removed = await attachmentRemovals.remove(chip.id, Boolean(chip.persisted));
    if (!removed) {
      enqueueSnackbar(t("Failed to remove attachment."), ERROR_SNACKBAR);
      return;
    }

    setAttachmentChipsByTask((prev) => ({
      ...prev,
      [taskId]: (prev[taskId] ?? []).filter((c) => c.id !== chip.id),
    }));
  };

  const handleDescriptionChange = (taskId: string, value: string) => {
    setTaskDescriptions((prev) => ({ ...prev, [taskId]: value }));
  };

  const handleRemarksChange = (taskId: string, value: string) => {
    setTaskRemarks((prev) => ({ ...prev, [taskId]: value }));
  };

  const openRemarks = (taskId: string) => {
    setRemarksOpen((prev) => ({ ...prev, [taskId]: true }));
    setRemarksFocusTaskId(taskId);
  };

  // Empties the remarks and folds the field back; saving clears them on the task.
  const removeRemarks = (taskId: string) => {
    handleRemarksChange(taskId, "");
    setRemarksOpen((prev) => ({ ...prev, [taskId]: false }));
  };

  const handleCounterChange = (equipmentId: string | number, kind: string, data: Record<string, unknown>) => {
    setCounterFormData((prev) => ({ ...prev, [`${equipmentId}:${kind}`]: data }));
  };

  const addUsedPartRow = (taskId: string) =>
    setUsedPartRowsByTask((prev) => ({
      ...prev,
      [taskId]: [...(prev[taskId] ?? []), { part: null, quantity: "" }],
    }));

  const removeUsedPartRow = (taskId: string, index: number) =>
    setUsedPartRowsByTask((prev) => ({
      ...prev,
      [taskId]: (prev[taskId] ?? []).filter((_, i) => i !== index),
    }));

  const updateUsedPartRow = (
    taskId: string,
    index: number,
    patch: Partial<{ part: OptionRecord | null; quantity: string }>
  ) =>
    setUsedPartRowsByTask((prev) => ({
      ...prev,
      [taskId]: (prev[taskId] ?? []).map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

  // The value a task's equipment already has assigned for this counter kind,
  // if any - used to tell "the tech actually changed this" apart from "this
  // is just the prefilled value re-submitted unchanged", so we don't send
  // (and the backend doesn't bump read_at for) counters nobody touched.
  const getAssignedCounterValue = (equipmentId: string | number, kind: string): string | undefined => {
    const task = tasks.find((t) => t.equipment && String(t.equipment.id) === String(equipmentId));
    return task?.counters.find((counter) => counter.kind === kind)?.value;
  };

  const buildSavePayload = (): JobStatusTransitionSaveDataPayload => {
    // Each task sends what its own card shows - a requirement can add a field
    // to one task alone.
    const shownByTask: Record<string, Set<string>> = Object.fromEntries(
      tasks.map((task) => [task.id, new Set(subFieldsFor(task).map(({ field }) => field.field_type))])
    );
    const shownAnywhere = (fieldType: string) => tasks.some((task) => shownByTask[task.id].has(fieldType));

    const task_updates = tasks
      .map((task) => {
        const shown = shownByTask[task.id];
        return {
          id: task.id,
          ...(shown.has("S")
            ? {
                services: (taskServices[task.id] ?? []).map((o) => o.id),
                service_quantities: toServiceQuantities(taskServices[task.id] ?? []),
              }
            : {}),
          ...(shown.has("I") ? { issues: (taskIssues[task.id] ?? []).map((o) => o.id) } : {}),
          ...(shown.has("T") ? { description: taskDescriptions[task.id] ?? "" } : {}),
          ...(shown.has("N") ? { remarks: taskRemarks[task.id] ?? "" } : {}),
          // Uploaded files start out unbound; sending their ids here is what
          // ties them to this task. Already-bound ones are ignored by the
          // backend, so resending the full chip list each save is harmless.
          ...(shown.has("A")
            ? {
                attachment_ids: (attachmentChipsByTask[task.id] ?? []).map(
                  (chip) => chip.id
                ),
              }
            : {}),
        };
      })
      .filter((update) => Object.keys(update).length > 1);

    const counter_entries = shownAnywhere("V")
      ? Object.values(counterFormData)
          .filter(
            (data) =>
              data.equipment_id != null && data.kind && String(data.value ?? "").trim() !== ""
          )
          .filter((data) => {
            const assignedValue = getAssignedCounterValue(
              data.equipment_id as string | number,
              String(data.kind)
            );
            if (assignedValue === undefined) {
              return true;
            }

            const parsedNew = parseCounterValue(data.value);
            const parsedAssigned = parseCounterValue(assignedValue);
            return parsedNew === null || parsedAssigned === null || parsedNew !== parsedAssigned;
          })
          .map((data) => ({
            equipment_id: data.equipment_id as string | number,
            kind: String(data.kind),
            value: String(data.value),
          }))
      : [];

    // `undefined` (rather than []) when no card shows used parts, so the
    // backend can tell "not part of this request" apart from "the tech removed
    // every row" - only the latter should delete used parts. The backend syncs
    // the whole job's used parts from this list, so every task's rows go along,
    // including those of cards that don't show the field (as they were loaded).
    const used_part_entries = shownAnywhere("U")
      ? tasks.flatMap((task) =>
          (usedPartRowsByTask[task.id] ?? [])
            .filter((row) => row.part && String(row.quantity).trim() !== "")
            .map((row) => ({
              job_task_id: task.id,
              part_id: (row.part as OptionRecord).id,
              quantity: toApiDecimal(String(row.quantity)),
            }))
        )
      : undefined;

    return { task_updates, counter_entries, used_part_entries };
  };

  // Files the user took off the list are deleted only once the save has gone
  // through, so a mistaken click costs nothing if the form is closed instead.
  const applyPendingRemovals = async () => {
    if (!(await attachmentRemovals.commit())) {
      enqueueSnackbar(t("Failed to remove attachment."), ERROR_SNACKBAR);
    }
  };

  const handleSave = async () => {
    if (await onSave(buildSavePayload())) {
      await applyPendingRemovals();
    }
  };

  const handleConfirmClick = async () => {
    const saved = await onSave(buildSavePayload());
    if (!saved) {
      return;
    }

    await applyPendingRemovals();

    const actionsData: Record<string, string> = {};
    if (rejectionReasonField) {
      actionsData[String(rejectionReasonField.id)] = rejectionReason;
    }

    onConfirm(actionsData);
  };

  const renderTaskSubField = (task: JobTask, key: TaskSubFieldKey, field: StatusTransitionRequiredField) => {
    const requiringServices = field.is_required ? [] : servicesRequiring(field.field_type, task);
    const isRequired = field.is_required || requiringServices.length > 0;
    const requiredReason =
      requiringServices.length > 0 ? requiredForServicesText(requiringServices) : undefined;
    const errorMessage = getFieldErrorMessage(field, task.id);

    if (key === "services") {
      return (
        <Box key="services" sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="body2">{t("Services")}</Typography>
            <ObligatoryChip isRequired={isRequired} reason={requiredReason} t={t} />
          </Box>
          <TaskServicesField
            value={taskServices[task.id] ?? []}
            onChange={(services) => setTaskServices((prev) => ({ ...prev, [task.id]: services }))}
            hoursWorked={inProgressHours(statusTimeline)}
            disabled={loading || saving}
          />
          {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </Box>
      );
    }

    if (key === "issues") {
      return (
        <Box key="issues" sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="body2">{t("Issues")}</Typography>
            <ObligatoryChip isRequired={isRequired} reason={requiredReason} t={t} />
          </Box>
          <CustomAutocomplete
            multiple
            filterSelectedOptions
            apiConfig={{ url: JOB_ISSUES_ENDPOINT }}
            value={taskIssues[task.id] ?? []}
            onChange={(_, newValue) =>
              setTaskIssues((prev) => ({ ...prev, [task.id]: newValue as OptionRecord[] }))
            }
            getOptionLabel={(option) => option.title}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label={t("Issues")} size="small" />}
            disabled={loading || saving}
          />
          {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </Box>
      );
    }

    if (key === "description") {
      return (
        <Box key="description" sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="body2">{t("Description")}</Typography>
            <ObligatoryChip isRequired={isRequired} reason={requiredReason} t={t} />
          </Box>
          <TextField
            value={taskDescriptions[task.id] ?? ""}
            onChange={(event) => handleDescriptionChange(task.id, event.target.value)}
            disabled={loading || saving}
            multiline
            minRows={2}
            fullWidth
          />
          {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </Box>
      );
    }

    if (key === "remarks") {
      const remarks = taskRemarks[task.id] ?? "";

      if (!isRequired && !remarksOpen[task.id]) {
        return (
          <Box key="remarks" sx={{ mb: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => openRemarks(task.id)}
              disabled={loading || saving}
            >
              {t("Add remarks")}
            </Button>
          </Box>
        );
      }

      return (
        <Box key="remarks" sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <WarningAmberIcon fontSize="small" color="warning" sx={{ mr: 0.5 }} />
            <Typography variant="body2">{t("Remarks")}</Typography>
            <ObligatoryChip isRequired={isRequired} reason={requiredReason} t={t} />
            {!isRequired && (
              <Tooltip title={t("Remove remarks")}>
                <span style={{ marginLeft: "auto" }}>
                  <IconButton
                    size="small"
                    aria-label={t("Remove remarks")}
                    onClick={() => removeRemarks(task.id)}
                    disabled={loading || saving}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <TextField
            value={remarks}
            onChange={(event) => handleRemarksChange(task.id, event.target.value)}
            disabled={loading || saving}
            color="warning"
            autoFocus={remarksFocusTaskId === task.id}
            multiline
            minRows={2}
            fullWidth
          />
          {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </Box>
      );
    }

    if (key === "usedParts") {
      const rows = usedPartRowsByTask[task.id] ?? [];
      return (
        <Box key="usedParts" sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="body2">{t("Used parts")}</Typography>
            <ObligatoryChip isRequired={isRequired} reason={requiredReason} t={t} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {rows.map((row, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <Box sx={{ flex: 2 }}>
                  <CustomAutocomplete
                    apiConfig={{ url: PARTS_ENDPOINT, queryParams: { in_stock: "true", is_active: "true" } }}
                    value={row.part}
                    onChange={(_, newValue) =>
                      updateUsedPartRow(task.id, index, { part: newValue as OptionRecord | null })
                    }
                    getOptionLabel={(option) => (option.code ? `${option.name} (${option.code})` : option.name)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label={t("Part")} size="small" />}
                    disabled={loading || saving}
                  />
                </Box>
                <TextField
                  size="small"
                  label={t("Quantity")}
                  value={row.quantity}
                  onChange={(event) => updateUsedPartRow(task.id, index, { quantity: event.target.value })}
                  disabled={loading || saving}
                  inputProps={{ inputMode: "decimal" }}
                  sx={{ flex: 1 }}
                />
                <IconButton onClick={() => removeUsedPartRow(task.id, index)} color="error" disabled={loading || saving}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={() => addUsedPartRow(task.id)}
              variant="outlined"
              size="small"
              sx={{ alignSelf: "flex-start" }}
              disabled={loading || saving}
            >
              {t("Add used part")}
            </Button>
          </Box>
          {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </Box>
      );
    }

    if (key === "attachments") {
      const chips = attachmentChipsByTask[task.id] ?? [];
      return (
        <Box key="attachments" sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="body2">{t("Attachments")}</Typography>
            <ObligatoryChip isRequired={isRequired} reason={requiredReason} t={t} />
          </Box>
          <FileDropZoneField
            onFileSelected={(file: File) => handleAttachmentFile(task.id, file)}
            uploading={attachmentUploadingTaskId === task.id}
            disabled={attachmentUploadingTaskId !== null || loading || saving}
          />
          {chips.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
              {chips.map((chip) => {
                // Read-only rather than hidden: the technician still needs to
                // see what was reported, they just cannot remove it. The
                // backend refuses either way.
                const readOnly = isServiceRole && chip.origin === "R";
                const chipElement = (
                  <Chip
                    key={chip.id}
                    label={chip.label}
                    size="small"
                    clickable={Boolean(chip.url)}
                    onClick={
                      chip.url
                        ? (event) => {
                            event.preventDefault();
                            window.open(chip.url, "_blank", "noopener,noreferrer");
                          }
                        : undefined
                    }
                    onDelete={
                      readOnly
                        ? undefined
                        : (event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            void handleRemoveAttachment(task.id, chip);
                          }
                    }
                    deleteIcon={readOnly ? undefined : <CloseIcon fontSize="small" />}
                  />
                );

                return readOnly ? (
                  <Tooltip key={chip.id} title={t("Attached when the job was reported.")}>
                    <span>{chipElement}</span>
                  </Tooltip>
                ) : (
                  chipElement
                );
              })}
            </Box>
          )}
          {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </Box>
      );
    }

    if (!task.equipment) {
      return null;
    }

    return (
      <Box key="counters" sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          <Typography variant="body2">{t("Counter values")}</Typography>
          <ObligatoryChip isRequired={isRequired} reason={requiredReason} t={t} />
        </Box>
        <EquipmentCounterFields
          equipment={task.equipment}
          assignedCounters={task.counters}
          jobId={jobId}
          onChange={(kind, data) => handleCounterChange(task.equipment!.id, kind, data)}
        />
        {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
      </Box>
    );
  };

  const renderEquipmentNotice = (task: JobTask) => {
    const serviceTitles = equipmentRequiredBy(task);
    const error = requirementErrorFor("E", task.id);
    if (serviceTitles.length === 0 && !error) {
      return null;
    }

    return (
      <Alert severity={error ? "error" : "warning"} sx={{ mb: 1.5 }}>
        <AlertTitle>{t("Equipment required")}</AlertTitle>
        {error ?? requiredForServicesText(serviceTitles)}{" "}
        {t("Set the equipment in the job, then change the status.")}
      </Alert>
    );
  };

  type Block = { key: string; isRequired: boolean; node: ReactNode };
  const blocks: Block[] = [];

  // A card for every task with something to fill in or a notice to show.
  const tasksWithCards = tasks.filter(
    (task) =>
      subFieldsFor(task).length > 0 ||
      equipmentRequiredBy(task).length > 0 ||
      Boolean(requirementErrorFor("E", task.id))
  );

  if (tasksWithCards.length > 0) {
    blocks.push({
      key: "tasks",
      isRequired: tasksWithCards.some(
        (task) =>
          equipmentRequiredBy(task).length > 0 ||
          subFieldsFor(task).some(({ field }) => isRequiredFor(field, task))
      ),
      node: tasksWithCards.map((task) => (
        <Box
          key={task.id}
          component="fieldset"
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2, pt: 1.5 }}
        >
          <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 0.5 }}>
            {equipmentLabel(task.equipment, t)}
          </Typography>
          {task.equipment && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              {(() => {
                const installationAddress =
                  formatAddress(task.equipment.installation_address) || tenantName;
                const reminderEmail = task.equipment.installation_address?.email_counter_reminders;
                const reminderText = reminderEmail
                  ? ` • ${t("Email for counter reminders")}: ${reminderEmail}`
                  : "";

                return (
                  <>
                    {[task.equipment.model, task.equipment.serial].filter(Boolean).join(" · ")}
                    {installationAddress ? ` — ${installationAddress}` : ""}
                    {reminderText}
                  </>
                );
              })()}
            </Typography>
          )}
          {renderEquipmentNotice(task)}
          {subFieldsFor(task).map(({ key, field }) => renderTaskSubField(task, key, field))}
        </Box>
      )),
    });
  }

  if (rejectionReasonField) {
    blocks.push({
      key: "rejectionReason",
      isRequired: rejectionReasonField.is_required,
      node: (
        <Box component="fieldset" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2, pt: 1.5 }}>
          <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 0.5, display: "flex", alignItems: "center" }}>
            {rejectionReasonField.checkbox_label || t("Rejection reason")}
            <ObligatoryChip isRequired={rejectionReasonField.is_required} t={t} />
          </Typography>
          <TextField
            value={rejectionReason}
            onChange={(event) => setRejectionReason(event.target.value)}
            disabled={loading || saving}
            multiline
            minRows={2}
            fullWidth
          />
          {getFieldErrorMessage(rejectionReasonField) && (
            <FormHelperText error>{getFieldErrorMessage(rejectionReasonField)}</FormHelperText>
          )}
        </Box>
      ),
    });
  }

  const orderedBlocks = [...blocks].sort((a, b) => Number(b.isRequired) - Number(a.isRequired));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, px: 0.5 }}>
      {showMissingEquipmentWarning && (
        <Alert severity="warning">
          {t(
            "This job has {{count}} task(s) without equipment assigned. Make sure this is intentional before moving to verification.",
            { count: tasksMissingEquipment.length }
          )}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary">
        {t("Fill in the fields below, then save your progress or confirm to change the job status.")}
      </Typography>

      {orderedBlocks.map((block) => (
        <Fragment key={block.key}>{block.node}</Fragment>
      ))}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "flex-end",
          pt: 2,
          mt: 0.5,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button variant="outlined" onClick={onClose} disabled={loading || saving}>
          {t("Cancel")}
        </Button>
        <Button variant="outlined" onClick={handleSave} disabled={loading || saving}>
          {saving ? t("Saving...") : t("Save")}
        </Button>
        <Button variant="contained" onClick={handleConfirmClick} disabled={loading || saving}>
          {loading ? t("Processing...") : t("Accept all & change status")}
        </Button>
      </Box>
    </Box>
  );
};

export default JobStatusTransitionActionsModal;
